import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { PAGE_KEYS, PageKey, isPageKey } from '../auth/page-access';
import { PAGE_ROUTES } from '../routing/app-paths';

/** Each page's legacy path, relative to `environment.legacyPortalUrl`. */
export const LEGACY_PATHS: Readonly<Record<PageKey, string>> = {
  'order-entry': 'Sales/PurchasePage.aspx',
  'edit-profile': 'Sales/ChangeInfo.aspx',
  'create-invoice': 'Sales/CreateInvoice.aspx',
  'sales-dashboard': 'Sales/Sales.aspx',
  'view-invoice-sales': 'Sales/ViewInvoice.aspx',
  'discount-page': 'Sales/DiscountPage.aspx',
  codes: 'Sales/Codes.aspx',
  'users-discount-code': 'Sales/UsersDiscountCode.aspx',
  'request-code': 'Sales/RequestCode.aspx',
  'users-enrollment': 'Sales/UsersEnrollment.aspx',
  affiliates: 'Corporate/Affiliates.aspx',
  'edit-affiliate': 'Corporate/EditAffiliate.aspx',
  'student-lookup': 'Sales/StudentLookup.aspx',
  corporates: 'Corporate/Corporates.aspx',
  'add-corporate-account': 'Corporate/addCorporateAccount.aspx',
  'corp-account-sites': 'Corporate/CorpAccountSites.aspx',
  'corporate-sites': 'Corporate/CorporateSites.aspx',
  'widget-details': 'Corporate/WidgetDetails.aspx',
  'view-invoice-corporate': 'Corporate/ViewInvoice.aspx',
  archive: 'Corporate/Archive.aspx',
};

type ParamValue = string | number;

/** What a page passes when it links to another page. */
export interface PageLinkOptions {
  /** Values for the route's `:params` (e.g. `{ id: 12 }` for `edit-affiliate/:id`). */
  params?: Readonly<Record<string, ParamValue>>;
  /** Query string for the new app's route. */
  queryParams?: Readonly<Record<string, ParamValue>>;
  /** Query string for the legacy page, in the legacy page's own parameter names. */
  legacyQueryParams?: Readonly<Record<string, ParamValue>>;
}

/** A link resolved for a template: a router link inside this app, or `href="#"` for a page not built here yet. */
export type PageLink =
  | { kind: 'route'; path: string; queryParams: Record<string, ParamValue> }
  | { kind: 'legacy'; href: string };

/**
 * The one place that knows whether a page is built here yet. Pages take outbound links from here; adding a key to
 * `environment.flippedPages` repoints every link to it.
 */
@Injectable({ providedIn: 'root' })
export class LinkRegistry {
  private readonly flipped: ReadonlySet<PageKey> = readFlippedPages(environment.flippedPages);

  link(page: PageKey, options: PageLinkOptions = {}): PageLink {
    if (this.flipped.has(page)) {
      return {
        kind: 'route',
        path: `/${fillParams(PAGE_ROUTES[page], options.params ?? {})}`,
        queryParams: { ...(options.queryParams ?? {}) },
      };
    }

    // No link goes to the legacy portal (EP-33). LEGACY_PATHS and `legacyQueryParams` stay so those links can return.
    return { kind: 'legacy', href: '#' };
  }
}

/** A misspelt key would silently leave a page linking to `#`, so fail loudly. */
function readFlippedPages(configured: readonly string[]): ReadonlySet<PageKey> {
  const unknown = configured.filter((key) => !isPageKey(key));

  if (unknown.length > 0) {
    throw new Error(
      `environment.flippedPages has unknown page keys: ${unknown.join(', ')}. ` +
        `Use keys from PAGE_KEYS: ${PAGE_KEYS.join(', ')}.`,
    );
  }

  return new Set(configured.filter(isPageKey));
}

function fillParams(path: string, params: Readonly<Record<string, ParamValue>>): string {
  return path.replace(/:([A-Za-z]+)/g, (_match, name: string) => {
    const value = params[name];

    if (value === undefined) {
      throw new Error(`The link to "${path}" needs a value for :${name}.`);
    }

    return encodeURIComponent(String(value));
  });
}
